import { useEffect, useRef } from "react";

const COLORS = [[65, 105, 225], [64, 224, 208], [255, 111, 181], [248, 249, 250]];

export default function HeroCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let w = 0;
    let h = 0;
    let rot = 0;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    const pts = [];
    for (let i = 0; i < 150; i++) {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / 150);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      pts.push({
        x: Math.sin(phi) * Math.cos(theta),
        y: Math.sin(phi) * Math.sin(theta),
        z: Math.cos(phi),
        hot: i % 6 === 0,
        ci: i % COLORS.length,
      });
    }

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMouse = (e) => {
      mouse.tx = 2 * (e.clientX / window.innerWidth - 0.5);
      mouse.ty = 2 * (e.clientY / window.innerHeight - 0.5);
    };

    const draw = () => {
      mouse.x += 0.05 * (mouse.tx - mouse.x);
      mouse.y += 0.05 * (mouse.ty - mouse.y);
      rot += 0.0018;
      ctx.clearRect(0, 0, w, h);

      const radius = 0.38 * Math.min(w, h);
      const cx = w * (w < 768 ? 0.5 : 0.74) + 26 * mouse.x;
      const cy = h * (w < 768 ? 0.3 : 0.42) + 26 * mouse.y;
      const tilt = 0.35 + 0.15 * mouse.y;

      const proj = pts.map((p) => {
        const rx = p.x * Math.cos(rot) - p.z * Math.sin(rot);
        const rz = p.x * Math.sin(rot) + p.z * Math.cos(rot);
        const ry = p.y * Math.cos(tilt) - rz * Math.sin(tilt);
        const rz2 = p.y * Math.sin(tilt) + rz * Math.cos(tilt);
        const s = 2.4 / (2.4 + rz2);
        return { sx: cx + rx * radius * s, sy: cy + ry * radius * s, z: rz2, hot: p.hot, ci: p.ci };
      });

      const linkDist = 0.34 * radius;
      ctx.lineWidth = 1;
      for (let i = 0; i < proj.length; i++) {
        for (let j = i + 1; j < proj.length; j++) {
          const dx = proj[i].sx - proj[j].sx;
          const dy = proj[i].sy - proj[j].sy;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDist * linkDist) {
            const alpha = 0.14 * (1 - Math.sqrt(d2) / linkDist) * ((proj[i].z + proj[j].z + 2) / 4 + 0.4);
            ctx.strokeStyle = `rgba(141,153,174,${alpha})`;
            ctx.beginPath();
            ctx.moveTo(proj[i].sx, proj[i].sy);
            ctx.lineTo(proj[j].sx, proj[j].sy);
            ctx.stroke();
          }
        }
      }

      for (const p of proj) {
        const depth = (p.z + 1) / 2;
        if (p.hot) {
          const c = COLORS[p.ci];
          ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${0.3 + 0.7 * depth})`;
          ctx.beginPath();
          ctx.arc(p.sx, p.sy, 2.6, 0, Math.PI * 2);
        } else {
          ctx.fillStyle = `rgba(171,193,215,${0.12 + 0.5 * depth})`;
          ctx.beginPath();
          ctx.arc(p.sx, p.sy, 1.4, 0, Math.PI * 2);
        }
        ctx.fill();
      }

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouse);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-canvas"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-70 md:opacity-100"
    />
  );
}
